/**
 * 🎮🔒 DragoniousCheatWall — Owner-only Cheat Codes Wall overlay
 *
 * Canon: /app/memory/DRAGONIOUS_FIGHTERS_SECRET_DEBUG_CHEAT_MENUS_CANON_FEB2026.md
 * & /app/memory/DRAGONIOUS_EGG_TIER_SPLIT_STAGES_CHEAT_WALL_CANON_FEB2026.md
 *
 * Renders the four hidden Dragonious Fighters cheat menus (Phoenix Nest,
 * Dragon Sovereign, OmniMuscle Underground, Purple Stuff) with:
 *   • Access-code sniffer — types directional inputs (↑↓←→) + button
 *     shortcut keys (J/K/L/;/B). When the buffered sequence matches a
 *     menu's access code, the corresponding drawer opens.
 *   • Manual selector — clickable menu tabs (owner already knows the
 *     codes; the tabs are a convenience).
 *   • Per-option state toggles — every option's canonical states
 *     (Off/On/P1/P2/etc.) are surfaced as pill buttons. `persistence:
 *     per_match` state is kept client-side and reset on unmount.
 *
 * Silent to non-owners — the backend returns 404 on `/cheat-wall` for
 * anyone but the owner.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { X, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Key → input-token map for the keyboard sniffer ──
const KEY_TO_TOKEN = {
  ArrowUp:    "UP",
  ArrowDown:  "DOWN",
  ArrowLeft:  "LEFT",
  ArrowRight: "RIGHT",
  KeyJ: "LP", KeyK: "HP", KeyL: "LK", Semicolon: "HK",
  KeyB: "BLOCK",
};

const TOKEN_GLYPH = {
  UP: "↑", DOWN: "↓", LEFT: "←", RIGHT: "→",
  LP: "👊", HP: "💥", LK: "🦵", HK: "💢", BLOCK: "🛡️",
};

export default function DragoniousCheatWall({ open, onClose }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [states, setStates] = useState({});          // { menuId: { optionId: state } }
  const [buffer, setBuffer] = useState([]);          // last 16 tokens typed
  const [lastMatch, setLastMatch] = useState(null);  // { at, menu_id }
  const bufferRef = useRef(buffer);
  bufferRef.current = buffer;

  // ── Load menu registry on open ────────────────────────────
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/dragonious-egg/cheat-wall`, { withCredentials: true })
      .then((r) => {
        setPayload(r.data);
        // Seed per-match states with defaults
        const seed = {};
        for (const m of r.data.menus || []) {
          seed[m.id] = { ...(r.data.default_states?.[m.id] || {}) };
        }
        setStates(seed);
        if (!activeMenuId && r.data.menus?.length) setActiveMenuId(r.data.menus[0].id);
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setError("Not available for this account.");
        } else {
          setError("Failed to load the Cheat Wall.");
        }
      })
      .finally(() => setLoading(false));
  }, [open]);

  // ── Keyboard sniffer: append tokens, verify on every keystroke ──
  useEffect(() => {
    if (!open || !payload?.granted) return;
    const handler = (e) => {
      // Ignore keys typed into inputs
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const token = KEY_TO_TOKEN[e.code];
      if (!token) return;
      e.preventDefault();
      const nextBuf = [...bufferRef.current, token].slice(-16);
      setBuffer(nextBuf);
      // Fire-and-forget verify (bounded to last 16 keystrokes so any
      // code up to length 16 will match if it lines up).
      axios.post(
        `${API}/dragonious-egg/cheat-wall/verify`,
        { inputs: nextBuf },
        { withCredentials: true }
      ).then((r) => {
        if (r.data.matched) {
          setActiveMenuId(r.data.menu.id);
          setLastMatch({ at: Date.now(), menu_id: r.data.menu.id });
          setBuffer([]); // consume the buffer
        } else {
          // Try progressively shorter tail-slices in case the buffer is
          // longer than the code (e.g. user typed extra junk before).
          const tails = [];
          for (let n = 3; n <= nextBuf.length; n++) {
            tails.push(nextBuf.slice(-n));
          }
          for (const tail of tails) {
            const res = _tryLocalMatch(tail, payload.menus);
            if (res) {
              setActiveMenuId(res.id);
              setLastMatch({ at: Date.now(), menu_id: res.id });
              setBuffer([]);
              break;
            }
          }
        }
      }).catch(() => {});
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, payload?.granted, payload?.menus]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="cheat-wall-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col rounded-2xl border-2 border-fuchsia-500/50 bg-gradient-to-br from-purple-950/95 via-black/95 to-fuchsia-950/95 shadow-2xl"
        style={{ boxShadow: "0 0 80px rgba(217, 70, 239, 0.4)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-fuchsia-500/20">
          <div>
            <div className="flex items-center gap-2 text-fuchsia-300 text-[10px] uppercase tracking-[0.35em] font-bold">
              <KeyRound size={12} />
              Owner Debug · Cheat Codes Wall
            </div>
            <div className="text-2xl font-bold text-white mt-1">🎮🔒 Dragonious Fighters Cheat Menus</div>
            <div className="text-[11px] text-fuchsia-200/70 mt-1">
              Type an access code with ↑↓←→ + J (LP) / K (HP) / L (LK) / ; (HK) / B (BLOCK)
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="cheat-wall-close"
            className="text-neutral-400 hover:text-white p-1"
          >
            <X size={22} />
          </button>
        </div>

        {loading && <div className="py-16 text-center text-neutral-500">Loading Cheat Wall…</div>}
        {error && <div className="py-16 text-center text-red-400">{error}</div>}

        {!loading && !error && payload && !payload.granted && (
          <div className="py-16 text-center">
            <div className="text-5xl mb-3">🥚</div>
            <div className="text-white font-bold">Wall Locked</div>
            <div className="text-sm text-neutral-400 mt-1">Hatch one Dragonious Fighters Egg to unlock the wall.</div>
          </div>
        )}

        {!loading && !error && payload?.granted && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Buffer sniffer readout */}
            <div className="px-6 py-2 border-b border-fuchsia-500/10 bg-black/40 flex items-center gap-3 text-xs">
              <span className="text-fuchsia-300 uppercase tracking-[0.3em] text-[9px] font-bold">Buffer</span>
              <div className="flex-1 font-mono text-fuchsia-100 truncate" data-testid="cheat-wall-buffer">
                {buffer.length === 0 ? <span className="text-neutral-600 italic">— idle —</span> :
                  buffer.map((t, i) => <span key={i} className="inline-block mx-0.5">{TOKEN_GLYPH[t] || t}</span>)
                }
              </div>
              {lastMatch && Date.now() - lastMatch.at < 4000 && (
                <span className="text-emerald-400 text-[10px] uppercase tracking-widest animate-pulse">
                  ✓ Matched {lastMatch.menu_id.replace(/_/g, " ")}
                </span>
              )}
              <button
                onClick={() => setBuffer([])}
                className="text-neutral-400 hover:text-white text-[10px] uppercase tracking-widest"
                data-testid="cheat-wall-buffer-clear"
              >Clear</button>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-3 flex flex-wrap gap-2">
              {(payload.menus || []).map((m) => {
                const isActive = m.id === activeMenuId;
                return (
                  <button
                    key={m.id}
                    onClick={() => setActiveMenuId(m.id)}
                    data-testid={`cheat-wall-tab-${m.id}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest border transition ${
                      isActive
                        ? "border-fuchsia-400 bg-fuchsia-900/50 text-white"
                        : "border-neutral-800 bg-black/40 text-neutral-400 hover:border-fuchsia-500/40 hover:text-fuchsia-200"
                    }`}
                  >
                    <span className="mr-1">{m.emoji}</span>{m.name}
                  </button>
                );
              })}
            </div>

            {/* Active menu detail */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {(payload.menus || []).filter((m) => m.id === activeMenuId).map((m) => (
                <div key={m.id} data-testid={`cheat-wall-menu-${m.id}`}>
                  <div className="mb-3 rounded-lg border border-fuchsia-500/20 bg-black/40 p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-3xl">{m.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-lg font-bold text-white">{m.name}</div>
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest text-fuchsia-300">Code</span>
                          {(m.access_code || []).map((tok, i) => (
                            <span key={i} className="inline-flex items-center justify-center w-6 h-6 rounded border border-fuchsia-500/40 bg-fuchsia-900/40 text-white text-sm">
                              {TOKEN_GLYPH[tok] || tok}
                            </span>
                          ))}
                          <span className="ml-2 text-[10px] text-neutral-500">Persistence: {m.persistence}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {(m.options || []).map((opt) => {
                      const cur = states[m.id]?.[opt.id] ?? opt.default_state;
                      return (
                        <div
                          key={opt.id}
                          className="rounded-lg border border-neutral-800 bg-black/40 p-3"
                          data-testid={`cheat-wall-option-${opt.id}`}
                        >
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-bold text-white">{opt.label}</div>
                              {opt.alias && (
                                <div className="text-[10px] text-fuchsia-300/70 uppercase tracking-widest">({opt.alias})</div>
                              )}
                              <div className="text-xs text-neutral-400 leading-relaxed mt-1">{opt.effect}</div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {(opt.states || []).map((s) => {
                                const active = cur === s;
                                return (
                                  <button
                                    key={s}
                                    onClick={() => setStates((prev) => ({
                                      ...prev,
                                      [m.id]: { ...(prev[m.id] || {}), [opt.id]: s },
                                    }))}
                                    data-testid={`cheat-wall-option-${opt.id}-state-${s}`}
                                    className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border transition ${
                                      active
                                        ? "border-emerald-400 bg-emerald-900/40 text-emerald-200"
                                        : "border-neutral-700 bg-black/40 text-neutral-400 hover:border-emerald-500/40 hover:text-emerald-200"
                                    }`}
                                  >{s}</button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-3 border-t border-fuchsia-500/10 text-[10px] uppercase tracking-widest text-neutral-500 text-center">
              Per-match state · resets when this drawer closes
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Local mirror of the backend's `match_access_code` — used to give
 * instant feedback while the network round-trip is in flight.
 */
function _tryLocalMatch(inputs, menus) {
  if (!inputs?.length || !menus?.length) return null;
  const target = inputs.map((t) => String(t).toUpperCase());
  for (const m of menus) {
    const code = (m.access_code || []).map((t) => String(t).toUpperCase());
    if (code.length !== target.length) continue;
    let ok = true;
    for (let i = 0; i < code.length; i++) {
      if (code[i] !== target[i]) { ok = false; break; }
    }
    if (ok) return m;
  }
  return null;
}
